import React from 'react';
import {
  Badge,
  Box,
  HStack,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { useTranslation } from 'i18n/TranslationProvider';

const VEHICLE_STATUS_LABELS = {
  operable: 'Operable',
  operational: 'Operable',
  'non-operable': 'Non-operable',
  nonoperable: 'Non-operable',
  inoperable: 'Non-operable',
};

function formatLabel(value) {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getQuantity(item) {
  const parsed = Number(item?.quantity);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

function getOperationalState(item, category) {
  const rawStatus = item?.status || item?.vehicleStatus || '';
  const normalizedStatus = String(rawStatus).trim().toLowerCase();

  if (normalizedStatus && VEHICLE_STATUS_LABELS[normalizedStatus]) {
    return VEHICLE_STATUS_LABELS[normalizedStatus];
  }

  if (['cars', 'motorcycles', 'other_vehicles'].includes(category)) {
    return 'Status not set';
  }
  // Boats don't have an operable/non-operable badge — they have HIN + length.
  if (category === 'boats') {
    return null;
  }

  return null;
}

function getDimensions(item) {
  if (item?.dimensions && typeof item.dimensions === 'object') {
    const length = item.dimensions.length || item.dimensions.l;
    const width = item.dimensions.width || item.dimensions.w;
    const height = item.dimensions.height || item.dimensions.h;
    const parts = [length, width, height].filter(Boolean);
    if (parts.length) return `${parts.join(' x ')} cm`;
  }

  const parts = [item?.length, item?.width, item?.height].filter(Boolean);
  return parts.length ? `${parts.join(' x ')} cm` : null;
}

function getItemTitle(item, index, t, category) {
  const yearPrefix = item?.year ? `${item.year} ` : '';
  const vehicleLabel = [item?.make, item?.model].filter(Boolean).join(' ').trim();
  if (vehicleLabel) return `${yearPrefix}${vehicleLabel}`.trim();
  if (item?.petType) return item.petType;
  if (item?.itemDescription) return item.itemDescription;
  if (item?.category) return formatLabel(item.category);
  return t('Item {{index}}', { index: index + 1 });
}

function getItemFacts(item, t, category) {
  const facts = [];
  const isBoat = category === 'boats';

  // VIN / HIN — backend redacts for non-assigned or unpaid partners, so the
  // value only arrives for client owners, admins, and assigned+paid carriers.
  if (item?.vin) {
    const vinLabel = isBoat ? t('HIN') : t('VIN');
    facts.push(`${vinLabel}: ${item.vin}`);
  }
  if (item?.licensePlate) {
    const plateLabel = isBoat ? t('Registration') : t('License plate');
    facts.push(`${plateLabel}: ${item.licensePlate}`);
  }
  if (item?.serialNumber) facts.push(`${t('Serial no.')}: ${item.serialNumber}`);
  if (item?.lengthMeters) facts.push(t('Length: {{length}} m', { length: item.lengthMeters }));
  if (item?.bodyType) facts.push(t('Body type: {{type}}', { type: formatLabel(item.bodyType) }));
  if (item?.generation) facts.push(t('Generation: {{generation}}', { generation: item.generation }));
  if (item?.palletType) facts.push(t('Pallet type: {{type}}', { type: item.palletType }));
  if (item?.materialType) facts.push(t('Material: {{type}}', { type: item.materialType }));
  if (item?.freightType) facts.push(t('Freight type: {{type}}', { type: item.freightType }));
  if (item?.weight) facts.push(`${t('Weight')}: ${item.weight} kg`);
  if (item?.totalVolumeCbm) facts.push(t('Volume: {{volume}} m³', { volume: item.totalVolumeCbm }));
  if (item?.breed) facts.push(t('Breed: {{breed}}', { breed: item.breed }));
  if (item?.specialHandling) facts.push(t('Special handling: {{details}}', { details: item.specialHandling }));

  const dimensions = getDimensions(item);
  if (dimensions) facts.push(t('Size: {{dimensions}}', { dimensions }));

  if (item?.originFloor || item?.destFloor) {
    facts.push(t('Floors: {{from}} to {{to}}', { from: item.originFloor || '0', to: item.destFloor || '0' }));
  }

  if (item?.hasElevatorOrigin || item?.hasElevatorDest) {
    facts.push(
      t('Lift: {{origin}} / {{destination}}', {
        origin: item?.hasElevatorOrigin ? t('origin') : t('no origin'),
        destination: item?.hasElevatorDest ? t('destination') : t('no destination'),
      }),
    );
  }

  // Show itemDescription in facts when it's not already used as the title
  // (i.e., when the title came from make+model or petType instead).
  const hasDistinctTitle = !!(item?.make || item?.model) || !!item?.petType;
  if (hasDistinctTitle && item?.itemDescription) {
    facts.push(item.itemDescription);
  }

  return facts.slice(0, 12);
}

export function getItemCount(items = []) {
  return Array.isArray(items) ? items.length : 0;
}

export default function ListingItemsSection({
  items = [],
  category,
  title = 'Items',
  subtitle,
}) {
  const { t } = useTranslation();
  const count = getItemCount(items);
  const borderColor = useColorModeValue('rgba(191, 219, 254, 0.95)', 'rgba(96, 165, 250, 0.28)');
  const panelBg = useColorModeValue('rgba(239, 246, 255, 0.7)', 'rgba(30, 64, 175, 0.12)');
  const rowBg = useColorModeValue('rgba(255,255,255,0.92)', 'rgba(15, 23, 42, 0.55)');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const titleColor = useColorModeValue('navy.800', 'white');

  if (!count) return null;

  return (
    <Box
      border="1px solid"
      borderColor={borderColor}
      borderRadius="24px"
      bg={panelBg}
      p={4}
    >
      <HStack justify="space-between" align="flex-start" mb={4} spacing={3} flexWrap="wrap">
        <Box>
          <Text fontSize="xs" fontWeight="700" color={mutedColor} textTransform="uppercase" letterSpacing="0.08em">
             {t(title)}
          </Text>
        </Box>
        {subtitle ? (
          <Text fontSize="sm" color={mutedColor}>
            {subtitle}
          </Text>
        ) : null}
      </HStack>

      <VStack align="stretch" spacing={2}>
        {items.map((item, index) => {
          const operationalState = getOperationalState(item, category);
          const facts = getItemFacts(item, t, category);
          const itemTitle = getItemTitle(item, index, t, category);

          return (
            <Box
              key={item?._id || item?.id || `${itemTitle}-${index}`}
              bg={rowBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="16px"
              px={4}
              py={2}
            >
              <Box>
                <HStack justify="space-between" spacing={4}>
                  <Text fontSize="sm" fontWeight="800" color={titleColor}>
                    {itemTitle}
                  </Text>

                  <HStack spacing={2} flexShrink={0}>
                    <Badge variant="subtle" colorScheme="blue" borderRadius="full" px={2} py={0} fontSize="10px" fontWeight="800">
                      {t('QTY {{quantity}}', { quantity: getQuantity(item) })}
                    </Badge>
                    {operationalState ? (
                      <Badge
                        variant="subtle"
                        colorScheme={operationalState === 'Non-operable' ? 'red' : operationalState === 'Status not set' ? 'gray' : 'green'}
                        borderRadius="full"
                        px={2}
                        py={0}
                        fontSize="10px"
                        fontWeight="800"
                      >
                        {t(operationalState)}
                      </Badge>
                    ) : null}
                  </HStack>
                </HStack>

                {facts.length ? (
                  <Text fontSize="xs" color={mutedColor} mt={1}>
                    {facts.join(' • ')}
                  </Text>
                ) : null}
              </Box>
            </Box>
          );
        })}
      </VStack>
    </Box>
  );
}
